import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_create_voice_session,
  API_log_voice_assistant_interaction,
  API_voice_rag_search,
} from "../../../constants";

// Connection states
export const ConnectionState = {
  DISCONNECTED: "DISCONNECTED",
  CONNECTING: "CONNECTING",
  CONNECTED: "CONNECTED",
  ERROR: "ERROR",
};

// Activity states
export const ActivityState = {
  IDLE: "IDLE",
  LISTENING: "LISTENING",
  THINKING: "THINKING",
  SPEAKING: "SPEAKING",
};

const MAX_RECONNECT_ATTEMPTS = 3;
const SESSION_DURATION_MS = 60 * 60 * 1000; // 60 minutes
const SESSION_WARNING_MS = 55 * 60 * 1000; // 55 minutes
const SESSION_RECONNECT_MS = 59 * 60 * 1000; // 59 minutes
const VISIBILITY_RECONNECT_THRESHOLD_MS = 30 * 1000; // 30 seconds
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export default class RealtimeService {
  /**
   * @param {Object} callbacks
   * @param {function(string)} callbacks.onConnectionStateChange
   * @param {function(string)} callbacks.onActivityStateChange
   * @param {function(string)} callbacks.onUserTranscript
   * @param {function(string, boolean)} callbacks.onAssistantTranscript - (text, isFinal)
   * @param {function(Array)} callbacks.onAnnotations
   * @param {function(string)} callbacks.onError
   * @param {function(string)} callbacks.onWarning
   */
  constructor(callbacks) {
    this._callbacks = callbacks;
    this._pc = null;
    this._dc = null;
    this._audioElement = null;
    this._localStream = null;
    this._companyId = null;
    this._storeId = null;
    this._sessionId = null;

    // Reconnection
    this._reconnectAttempts = 0;
    this._reconnecting = false;

    // Session timer
    this._sessionTimers = [];

    // Barge-in tracking
    this._currentItemId = null;
    this._audioPlaybackMs = 0;
    this._audioPlaybackInterval = null;

    // Function call accumulators keyed by item_id
    this._pendingFunctionCalls = {};
    this._lastUserTranscript = "";
    this._currentResponseId = null;
    this._currentResponseHadFunctionCall = false;
    this._pendingAssistantTranscript = "";
    this._pendingAssistantResponseId = null;
    this._pendingVoiceAnnotations = [];
    this._pendingVoiceSearchCount = 0;
    this._lastVoiceLogKey = "";

    // Idle timeout
    this._idleTimer = null;

    // Visibility tracking
    this._hiddenTimestamp = null;
    this._boundVisibilityHandler = this._handleVisibilityChange.bind(this);

    // State
    this._connectionState = ConnectionState.DISCONNECTED;
    this._activityState = ActivityState.IDLE;
    this._isMuted = false;
    this._connectGeneration = 0;
  }

  get connectionState() {
    return this._connectionState;
  }

  get activityState() {
    return this._activityState;
  }

  _setConnectionState(state) {
    this._connectionState = state;
    this._callbacks.onConnectionStateChange?.(state);
  }

  _setActivityState(state) {
    this._activityState = state;
    this._callbacks.onActivityStateChange?.(state);
  }

  /**
   * Connect to the OpenAI Realtime API via WebRTC.
   * @param {string|number} companyId
   * @param {string|number} storeId
   */
  async connect(companyId, storeId) {
    this._companyId = companyId;
    this._storeId = storeId;
    this._reconnectAttempts = 0;
    this._connectGeneration++;
    document.addEventListener("visibilitychange", this._boundVisibilityHandler);
    await this._doConnect();
  }

  async _doConnect() {
    const generation = this._connectGeneration;
    this._setConnectionState(ConnectionState.CONNECTING);

    let stream;
    try {
      // 1. Get mic permission first (fail fast before spending an ephemeral token)
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      if (this._connectGeneration !== generation) return;
      this._setConnectionState(ConnectionState.ERROR);
      this._callbacks.onError?.("Microphone access is required for voice mode. Please allow microphone access and try again.");
      return;
    }

    // Check if cancelled while waiting for mic permission
    if (this._connectGeneration !== generation) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    this._localStream = stream;
    // Reapply mute state to the new stream (critical for reconnects)
    if (this._isMuted) {
      this._localStream.getAudioTracks().forEach((track) => {
        track.enabled = false;
      });
    }

    try {
      // 2. Get ephemeral token from backend
      const sessionResp = await postRightworkAPI(API_create_voice_session, {
        company_id: this._companyId,
      });

      if (this._connectGeneration !== generation) {
        this._releaseResources();
        return;
      }

      if (sessionResp.status !== 200) {
        throw new Error("Failed to create voice session");
      }

      const sessionData = sessionResp.data?.redux?.payload;
      const clientSecret = sessionData?.client_secret;
      const ephemeralKey = clientSecret?.value;
      this._sessionId = sessionData?.session_id || null;

      if (!ephemeralKey) {
        throw new Error("No ephemeral key received");
      }

      // 3. Create RTCPeerConnection
      this._pc = new RTCPeerConnection();

      // Add local audio track
      this._localStream.getTracks().forEach((track) => {
        this._pc.addTrack(track, this._localStream);
      });

      // 4. Set up remote audio playback — synchronous in ontrack for Safari autoplay
      this._audioElement = document.createElement("audio");
      this._audioElement.autoplay = true;
      this._pc.ontrack = (event) => {
        this._audioElement.srcObject = event.streams[0];
      };

      // 5. Monitor ICE connection state for reconnection
      this._pc.oniceconnectionstatechange = () => {
        const state = this._pc?.iceConnectionState;
        if (state === "disconnected") {
          // Wait 2s to see if it self-recovers
          setTimeout(() => {
            if (this._pc?.iceConnectionState === "disconnected") {
              this._attemptReconnect();
            }
          }, 2000);
        } else if (state === "failed") {
          this._attemptReconnect();
        }
      };

      // 6. Create data channel
      this._dc = this._pc.createDataChannel("oai-events");
      this._dc.onopen = () => {
        if (this._connectGeneration !== generation) return;
        this._reconnectAttempts = 0;
        this._setConnectionState(ConnectionState.CONNECTED);
        this._setActivityState(ActivityState.LISTENING);
        this._startSessionTimers();
        this._resetIdleTimer();
      };
      this._dc.onmessage = (event) => {
        this._handleDataChannelMessage(event);
      };
      this._dc.onclose = () => {
        if (this._connectionState === ConnectionState.CONNECTED) {
          this._attemptReconnect();
        }
      };

      // 7. Create SDP offer and connect
      const offer = await this._pc.createOffer();
      await this._pc.setLocalDescription(offer);

      if (this._connectGeneration !== generation) {
        this._releaseResources();
        return;
      }

      // GA WebRTC route (sessions are now minted via the GA
      // /v1/realtime/client_secrets endpoint — the beta mint was removed).
      const sdpResp = await fetch(
        "https://api.openai.com/v1/realtime/calls?model=gpt-realtime-1.5",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            "Content-Type": "application/sdp",
          },
          body: offer.sdp,
        }
      );

      if (this._connectGeneration !== generation) {
        this._releaseResources();
        return;
      }

      if (!sdpResp.ok) {
        throw new Error(`WebRTC SDP exchange failed: ${sdpResp.status}`);
      }

      const answerSdp = await sdpResp.text();
      await this._pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });

      // Start audio playback tracking for barge-in
      this._startPlaybackTracking();

    } catch (err) {
      if (this._connectGeneration !== generation) return;
      console.error("Voice connection error:", err);
      this._releaseResources();
      this._setConnectionState(ConnectionState.ERROR);
      this._callbacks.onError?.(
        err.response?.data?.system?.message || err.message || "Failed to connect to voice assistant"
      );
    }
  }

  /**
   * Disconnect and clean up all resources.
   */
  disconnect() {
    this._connectGeneration++;
    this._clearSessionTimers();
    this._stopPlaybackTracking();
    document.removeEventListener("visibilitychange", this._boundVisibilityHandler);
    this._releaseResources();
    this._setConnectionState(ConnectionState.DISCONNECTED);
    this._setActivityState(ActivityState.IDLE);
  }

  /**
   * Toggle microphone mute.
   * @param {boolean} muted
   */
  setMuted(muted) {
    this._isMuted = muted;
    if (this._localStream) {
      this._localStream.getAudioTracks().forEach((track) => {
        track.enabled = !muted;
      });
    }
  }

  // ===========================================================================
  // Data Channel Message Handler
  // ===========================================================================

  _handleDataChannelMessage(event) {
    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }

    const type = msg.type;

    switch (type) {
      case "input_audio_buffer.speech_started":
        this._handleBargeIn();
        this._setActivityState(ActivityState.LISTENING);
        this._resetIdleTimer();
        break;

      case "input_audio_buffer.speech_stopped":
        this._setActivityState(ActivityState.THINKING);
        this._callbacks.onSpeechStopped?.();
        break;

      case "conversation.item.input_audio_transcription.completed": {
        const transcript = msg.transcript || "";
        // Filter phantom transcripts caused by echo: short text while assistant is speaking.
        // Whisper hallucinations from echo are typically < 10 chars, often in foreign scripts.
        if (
          transcript.trim().length < 10 &&
          this._activityState === ActivityState.SPEAKING
        ) {
          break;
        }
        this._callbacks.onUserTranscript?.(transcript);
        this._lastUserTranscript = transcript.trim();
        this._pendingVoiceAnnotations = [];
        this._pendingVoiceSearchCount = 0;
        break;
      }

      case "response.created":
        this._currentResponseId = msg.response?.id || msg.response_id || null;
        this._currentResponseHadFunctionCall = false;
        this._pendingAssistantTranscript = "";
        this._pendingAssistantResponseId = null;
        this._setActivityState(ActivityState.THINKING);
        break;

      case "response.output_item.added":
        if (msg.item?.id) {
          this._currentItemId = msg.item.id;
        }
        // Initialize per-item function call accumulator
        if (msg.item?.type === "function_call") {
          this._currentResponseHadFunctionCall = true;
          this._pendingFunctionCalls[msg.item.id] = {
            name: msg.item.name || "",
            callId: msg.item.call_id || "",
            args: "",
          };
        }
        break;

      // Audio/transcript events arrive under GA names
      // (response.output_audio*) from GA-minted sessions; the legacy names
      // are kept as fallbacks.
      case "response.audio.delta":
      case "response.output_audio.delta":
        this._setActivityState(ActivityState.SPEAKING);
        break;

      case "response.audio_transcript.delta":
      case "response.output_audio_transcript.delta":
        this._callbacks.onAssistantTranscript?.(msg.delta || "", false);
        break;

      case "response.audio_transcript.done":
      case "response.output_audio_transcript.done": {
        const transcript = msg.transcript || "";
        this._callbacks.onAssistantTranscript?.(transcript, true);
        this._pendingAssistantTranscript = transcript;
        this._pendingAssistantResponseId =
          msg.response_id || this._currentResponseId || msg.item_id || this._currentItemId;
        break;
      }

      case "response.function_call_arguments.delta": {
        const pending = this._pendingFunctionCalls[msg.item_id];
        if (pending) {
          pending.args += msg.delta || "";
        }
        break;
      }

      case "response.function_call_arguments.done": {
        const itemId = msg.item_id;
        const completed = this._pendingFunctionCalls[itemId];
        if (completed) {
          delete this._pendingFunctionCalls[itemId];
          this._executeFunctionCall(
            completed.name || msg.name || "",
            completed.callId || msg.call_id || "",
            completed.args
          );
        }
        break;
      }

      case "response.done":
        if (!this._currentResponseHadFunctionCall) {
          this._logCompletedVoiceTurn(
            this._pendingAssistantTranscript,
            this._pendingAssistantResponseId ||
              msg.response?.id ||
              msg.response_id ||
              this._currentResponseId ||
              this._currentItemId
          );
        }
        this._currentResponseHadFunctionCall = false;
        this._pendingAssistantTranscript = "";
        this._pendingAssistantResponseId = null;
        // Only go back to LISTENING if we're not in the middle of a function call
        if (this._activityState !== ActivityState.THINKING) {
          this._setActivityState(ActivityState.LISTENING);
        }
        break;

      case "error":
        this._callbacks.onError?.(msg.error?.message || "An error occurred");
        break;

      default:
        break;
    }
  }

  // ===========================================================================
  // Barge-in
  // ===========================================================================

  _handleBargeIn() {
    if (this._activityState === ActivityState.SPEAKING && this._currentItemId) {
      // Truncate the current response
      this._sendDataChannelEvent("conversation.item.truncate", {
        item_id: this._currentItemId,
        content_index: 0,
        audio_end_ms: this._audioPlaybackMs,
      });
      this._sendDataChannelEvent("response.cancel", {});
    }
    this._audioPlaybackMs = 0;
  }

  _startPlaybackTracking() {
    this._audioPlaybackMs = 0;
    this._audioPlaybackInterval = setInterval(() => {
      if (this._activityState === ActivityState.SPEAKING) {
        this._audioPlaybackMs += 100;
      }
    }, 100);
  }

  _stopPlaybackTracking() {
    if (this._audioPlaybackInterval) {
      clearInterval(this._audioPlaybackInterval);
      this._audioPlaybackInterval = null;
    }
  }

  // ===========================================================================
  // Function Call Execution
  // ===========================================================================

  async _executeFunctionCall(name, callId, argsJson) {
    if (name !== "search_knowledge_base") {
      // Unknown function — send error output
      this._sendFunctionCallOutput(callId, JSON.stringify({ error: "Unknown function" }));
      return;
    }

    let query = "";
    try {
      const args = JSON.parse(argsJson);
      query = args.query || "";
    } catch {
      this._sendFunctionCallOutput(callId, JSON.stringify({ error: "Invalid arguments" }));
      return;
    }

    this._setActivityState(ActivityState.THINKING);

    try {
      const resp = await postRightworkAPI(API_voice_rag_search, {
        company_id: this._companyId,
        store_id: this._storeId,
        query: query,
      });

      const payload = resp.data?.redux?.payload;
      const context = payload?.context || "No results found.";
      const annotations = payload?.annotations || [];
      this._pendingVoiceSearchCount += 1;
      if (annotations.length > 0) {
        this._pendingVoiceAnnotations = [
          ...this._pendingVoiceAnnotations,
          ...annotations,
        ].slice(-20);
      }

      // Send context back to the model
      this._sendFunctionCallOutput(callId, context);

      // Send annotations to the UI for citation display
      if (annotations.length > 0) {
        this._callbacks.onAnnotations?.(annotations);
      }
    } catch (err) {
      console.error("RAG search error:", err);
      this._sendFunctionCallOutput(
        callId,
        "I had trouble searching documents right now. Please try asking again."
      );
    }
  }

  _logCompletedVoiceTurn(answerText, responseId) {
    const questionText = (this._lastUserTranscript || "").trim();
    const cleanAnswerText = (answerText || "").trim();
    if (!this._companyId || !questionText || !cleanAnswerText) {
      return;
    }

    const logKey = responseId || `${questionText}:${cleanAnswerText}`;
    if (this._lastVoiceLogKey === logKey) {
      return;
    }
    this._lastVoiceLogKey = logKey;

    const payload = {
      company_id: this._companyId,
      store_id: this._storeId,
      question_text: questionText,
      answer_text: cleanAnswerText,
      annotations: this._pendingVoiceAnnotations,
      search_count: this._pendingVoiceSearchCount,
      session_id: this._sessionId,
      response_id: responseId,
    };

    this._lastUserTranscript = "";
    this._pendingVoiceAnnotations = [];
    this._pendingVoiceSearchCount = 0;

    postRightworkAPI(API_log_voice_assistant_interaction, payload).catch((err) => {
      console.error("Voice interaction logging error:", err);
    });
  }

  _sendFunctionCallOutput(callId, output) {
    // Send the function call output
    this._sendDataChannelEvent("conversation.item.create", {
      item: {
        type: "function_call_output",
        call_id: callId,
        output: output,
      },
    });
    // Trigger a new response
    this._sendDataChannelEvent("response.create", {});
  }

  // ===========================================================================
  // Data Channel Helpers
  // ===========================================================================

  _sendDataChannelEvent(type, data) {
    if (this._dc && this._dc.readyState === "open") {
      this._dc.send(JSON.stringify({ type, ...data }));
    }
  }

  // ===========================================================================
  // Reconnection
  // ===========================================================================

  async _attemptReconnect() {
    if (this._reconnecting) return;
    this._reconnecting = true;

    const generation = this._connectGeneration;

    this._releaseResources();

    while (this._reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      this._reconnectAttempts++;

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, this._reconnectAttempts - 1) * 1000;
      this._callbacks.onWarning?.("Reconnecting...");

      await new Promise((resolve) => setTimeout(resolve, delay));

      // Bail out if disconnect() or connect() was called during the backoff wait
      if (this._connectGeneration !== generation) {
        this._reconnecting = false;
        return;
      }

      try {
        await this._doConnect();
      } catch {
        // _doConnect handles its own errors
      }

      // If _doConnect succeeded, stop retrying
      if (this._connectionState === ConnectionState.CONNECTED) {
        this._reconnecting = false;
        return;
      }

      // If cancelled during _doConnect, stop retrying
      if (this._connectGeneration !== generation) {
        this._reconnecting = false;
        return;
      }

      // Otherwise clean up before next attempt
      this._releaseResources();
    }

    // Exhausted all attempts
    this._reconnecting = false;
    this._setConnectionState(ConnectionState.ERROR);
    this._callbacks.onError?.("Connection lost. Please try reconnecting.");
  }

  // ===========================================================================
  // Visibility Change (iPad support)
  // ===========================================================================

  _handleVisibilityChange() {
    if (document.hidden) {
      this._hiddenTimestamp = Date.now();
    } else {
      if (this._hiddenTimestamp) {
        const hiddenDuration = Date.now() - this._hiddenTimestamp;
        this._hiddenTimestamp = null;

        if (hiddenDuration > VISIBILITY_RECONNECT_THRESHOLD_MS) {
          // Was hidden for >30s, proactively reconnect
          this._reconnectAttempts = 0;
          this._attemptReconnect();
        } else if (this._pc?.iceConnectionState === "failed" || this._pc?.iceConnectionState === "disconnected") {
          // Short hide but connection degraded
          this._reconnectAttempts = 0;
          this._attemptReconnect();
        }
      }
    }
  }

  // ===========================================================================
  // Session Timers
  // ===========================================================================

  _startSessionTimers() {
    this._clearSessionTimers();

    // Warning at 55 minutes
    this._sessionTimers.push(
      setTimeout(() => {
        this._callbacks.onWarning?.("Voice session ending soon");
      }, SESSION_WARNING_MS)
    );

    // Auto-reconnect at 59 minutes
    this._sessionTimers.push(
      setTimeout(() => {
        this._callbacks.onWarning?.("Session limit reached — reconnecting...");
        this._reconnectAttempts = 0;
        this._attemptReconnect();
      }, SESSION_RECONNECT_MS)
    );
  }

  _clearSessionTimers() {
    this._sessionTimers.forEach((t) => clearTimeout(t));
    this._sessionTimers = [];
  }

  // ===========================================================================
  // Idle Timeout
  // ===========================================================================

  _resetIdleTimer() {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
    }
    this._idleTimer = setTimeout(() => {
      if (this._connectionState === ConnectionState.CONNECTED) {
        this._callbacks.onIdleTimeout?.();
        this.disconnect();
      }
    }, IDLE_TIMEOUT_MS);
  }

  _clearIdleTimer() {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
      this._idleTimer = null;
    }
  }

  // ===========================================================================
  // Resource Cleanup
  // ===========================================================================

  _releaseResources() {
    this._stopPlaybackTracking();
    this._clearSessionTimers();
    this._clearIdleTimer();

    if (this._localStream) {
      this._localStream.getTracks().forEach((track) => track.stop());
      this._localStream = null;
    }

    if (this._dc) {
      try { this._dc.close(); } catch {}
      this._dc = null;
    }

    if (this._pc) {
      try { this._pc.close(); } catch {}
      this._pc = null;
    }

    if (this._audioElement) {
      this._audioElement.srcObject = null;
      this._audioElement = null;
    }

    this._currentItemId = null;
    this._currentResponseId = null;
    this._currentResponseHadFunctionCall = false;
    this._pendingAssistantTranscript = "";
    this._pendingAssistantResponseId = null;
    this._audioPlaybackMs = 0;
    this._pendingFunctionCalls = {};
    this._lastUserTranscript = "";
    this._pendingVoiceAnnotations = [];
    this._pendingVoiceSearchCount = 0;
    this._lastVoiceLogKey = "";
    this._sessionId = null;
  }
}
